import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  writeBatch 
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { auth } from '../lib/firebase/config';

// Types for data structures
interface Course {
  id: string;
  name: string;
  lectureCount: number;
  watchedLectures: number;
  createdAt: string;
  lastAccessed: string;
}

// Firebase service that provides user-scoped data access
class FirebaseService {
  private getUserId?: () => string;

  // Set the user ID provider - must be called before any operations
  setUserIdProvider(provider: () => string) {
    this.getUserId = provider;
  }

  private getCurrentUserId(): string {
    // First try to get the ID from the provider function
    if (this.getUserId) {
      try {
        const id = this.getUserId();
        if (id) return id;
      } catch (error) {
        console.error('Error getting user ID from provider:', error);
      }
    }
    
    // Fallback to directly checking current user from auth
    const currentUser = auth.currentUser;
    if (currentUser?.uid) {
      console.log('Using fallback auth.currentUser.uid:', currentUser.uid);
      return currentUser.uid;
    }
    
    throw new Error('Cannot get user ID. User may not be authenticated.');
  }

  // Generic methods for any document type
  async getDocument<T>(collectionName: string, docId: string, defaultValue: T): Promise<T> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as T;
      }
      return defaultValue;
    } catch (error) {
      console.error(`Error getting document ${docId} from ${collectionName}:`, error);
      return defaultValue;
    }
  }

  async setDocument(collectionName: string, docId: string, data: any): Promise<boolean> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, collectionName, docId);
      await setDoc(docRef, data);
      return true;
    } catch (error) {
      console.error(`Error setting document ${docId} in ${collectionName}:`, error);
      return false;
    }
  }

  async updateDocument(collectionName: string, docId: string, updates: any): Promise<boolean> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, collectionName, docId);
      await updateDoc(docRef, updates);
      return true;
    } catch (error) {
      console.error(`Error updating document ${docId} in ${collectionName}:`, error);
      return false;
    }
  }

  async deleteDocument(collectionName: string, docId: string): Promise<boolean> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, collectionName, docId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting document ${docId} from ${collectionName}:`, error);
      return false;
    }
  }

  async getCollection<T>(collectionName: string): Promise<T[]> {
    try {
      const userId = this.getCurrentUserId();
      const colRef = collection(db, 'users', userId, collectionName);
      const querySnapshot = await getDocs(colRef);
      
      const results: T[] = [];
      querySnapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as T);
      });
      
      return results;
    } catch (error) {
      console.error(`Error getting collection ${collectionName}:`, error);
      return [];
    }
  }

  // ===== STORAGE SERVICE COMPATIBILITY =====
  
  async getItem<T>(key: string, defaultValue: T | null = null): Promise<T | null> {
    const result = await this.getDocument('storage', key, { data: defaultValue });
    return result.data ?? defaultValue;
  }

  async setItem(key: string, value: any): Promise<boolean> {
    return this.setDocument('storage', key, { data: value, updatedAt: new Date().toISOString() });
  }

  async removeItem(key: string): Promise<boolean> {
    return this.deleteDocument('storage', key);
  }

  // ===== COURSE-SPECIFIC METHODS =====
  
  async getCourses(): Promise<Course[]> {
    return this.getCollection<Course>('courses');
  }

  async setCourses(courses: Course[]): Promise<boolean> {
    try {
      const userId = this.getCurrentUserId();
      const batch = writeBatch(db);
      
      const colRef = collection(db, 'users', userId, 'courses');
      const existingDocs = await getDocs(colRef);
      
      // Delete existing documents
      existingDocs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Add new courses
      courses.forEach((course) => {
        const docRef = doc(colRef, course.id);
        batch.set(docRef, course);
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error setting courses:', error);
      return false;
    }
  }

  // ===== PROJECT-SPECIFIC METHODS =====
  
  async getProjects(): Promise<any[]> {
    return this.getCollection('projects');
  }

  async saveProjects(projects: any[]): Promise<void> {
    try {
      const userId = this.getCurrentUserId();
      const batch = writeBatch(db);
      
      const colRef = collection(db, 'users', userId, 'projects');
      const existingDocs = await getDocs(colRef);
      
      // Delete existing documents
      existingDocs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Add new projects
      projects.forEach((project) => {
        const docRef = doc(colRef, project.id);
        batch.set(docRef, project);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error saving projects:', error);
      throw new Error('Failed to save projects');
    }
  }

  async createProject(project: any): Promise<any> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, 'projects', project.id);
      await setDoc(docRef, project);
      return project;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  async updateProject(id: string, updates: any): Promise<any> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, 'projects', id);
      await updateDoc(docRef, updates);
      
      // Return updated document
      const updatedDoc = await getDoc(docRef);
      return updatedDoc.exists() ? { id: updatedDoc.id, ...updatedDoc.data() } : null;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  async deleteProject(id: string): Promise<boolean> {
    return this.deleteDocument('projects', id);
  }

  async getProject(id: string): Promise<any | null> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, 'projects', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting project:', error);
      return null;
    }
  }

  async markWorkedOnToday(id: string): Promise<any> {
    try {
      const project = await this.getProject(id);
      if (!project) {
        throw new Error('Project not found');
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Check if already worked on today
      if (project.workDates.includes(today)) {
        return project; // Already marked for today
      }

      const updates = {
        lastWorkedOn: today,
        totalDaysWorked: project.totalDaysWorked + 1,
        workDates: [...project.workDates, today],
        updatedAt: new Date().toISOString(),
      };

      return this.updateProject(id, updates);
    } catch (error) {
      console.error('Error marking project as worked on today:', error);
      throw error;
    }
  }

  async undoWorkedOnToday(id: string): Promise<any> {
    try {
      const project = await this.getProject(id);
      if (!project) {
        throw new Error('Project not found');
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Check if project was actually worked on today
      if (!project.workDates.includes(today)) {
        return project; // Nothing to undo
      }

      // Remove today from workDates and decrement totalDaysWorked
      const updatedWorkDates = project.workDates.filter((date: string) => date !== today);
      const newTotalDaysWorked = Math.max(0, project.totalDaysWorked - 1);
      
      // Find the most recent work date for lastWorkedOn (excluding today)
      const mostRecentWorkDate = updatedWorkDates.length > 0 
        ? updatedWorkDates.sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())[0]
        : project.startDate; // Fall back to start date if no other work dates

      const updates = {
        lastWorkedOn: mostRecentWorkDate,
        totalDaysWorked: newTotalDaysWorked,
        workDates: updatedWorkDates,
        updatedAt: new Date().toISOString(),
      };

      return this.updateProject(id, updates);
    } catch (error) {
      console.error('Error undoing worked on today:', error);
      throw error;
    }
  }

  // ===== POMODORO-SPECIFIC METHODS =====
  
  async getPomodoroSettings<T>(defaultSettings: T): Promise<T> {
    return this.getDocument('pomodoro', 'settings', defaultSettings);
  }

  async setPomodoroSettings<T>(settings: T): Promise<T> {
    await this.setDocument('pomodoro', 'settings', settings);
    return settings;
  }

  async getPomodoroStats<T>(defaultStats: T): Promise<T> {
    return this.getDocument('pomodoro', 'stats', defaultStats);
  }

  async setPomodoroStats<T>(stats: T): Promise<void> {
    await this.setDocument('pomodoro', 'stats', stats);
  }

  // ===== USER PROFILE METHODS =====
  
  async getUserProfile<T>(defaultProfile: T): Promise<T> {
    return this.getDocument('profile', 'info', defaultProfile);
  }

  async setUserProfile<T>(profile: T): Promise<T> {
    await this.setDocument('profile', 'info', profile);
    return profile;
  }

  async updateUserProfile(updates: any): Promise<any> {
    try {
      const userId = this.getCurrentUserId();
      const docRef = doc(db, 'users', userId, 'profile', 'info');
      
      // Get current profile or create default
      const currentProfile = await this.getUserProfile({
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString()
      });
      
      const updatedProfile = {
        ...currentProfile,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(docRef, updatedProfile);
      return updatedProfile;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
}

// Export singleton instance
const firebaseService = new FirebaseService();
export default firebaseService; 